import { useState } from 'react';
import { POST_SUBMISSION_GRACE_DAYS } from '@affily/shared/constants';
import { formatCents } from '@affily/shared/money';
import { api } from '../lib/api.js';
import { useFetch } from '../hooks/useFetch.js';
import { Button, Badge, Input, Alert } from './ui.jsx';

export const postStatusTone = {
  submitted: 'caution',
  approved: 'positive',
  rejected: 'neutral',
  withdrawn: 'neutral',
};

export const postStatusLabel = {
  submitted: 'Waiting for approval',
  approved: 'Approved — paid',
  rejected: 'Not approved',
  withdrawn: 'Withdrawn',
};

/**
 * Pay per post, on the creator's partnership: send the link to each post you
 * made, and the brand approves it, which is what pays it.
 */
export function PartnershipPosts({ partnership }) {
  const { data, reload } = useFetch(`/posts?partnershipId=${partnership.id}`);
  const [url, setUrl] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const posts = data?.posts ?? [];
  const amount = formatCents(partnership.terms.flatAmountCents, { currency: partnership.terms.currency });

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    setFieldErrors({});
    try {
      await api.post('/posts', { partnershipId: partnership.id, url, note });
      setUrl('');
      setNote('');
      reload();
    } catch (err) {
      setError(err.message);
      setFieldErrors(err.details ?? {});
    } finally {
      setBusy(false);
    }
  }

  async function withdraw(id) {
    setError('');
    try {
      await api.post(`/posts/${id}/withdraw`);
      reload();
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <div className="border-t border-rule p-4">
      <p className="text-2xs font-semibold uppercase tracking-wider text-ink-faint">Posts · {amount} each, once approved</p>

      {partnership.status === 'active' && (
        <form onSubmit={submit} className="mt-2 flex flex-wrap items-start gap-2">
          <div className="min-w-64 flex-1">
            <Input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="Link to your post, e.g. https://www.tiktok.com/@you/video/…"
              aria-label="Link to your post"
              required
            />
            {fieldErrors.url && <span className="mt-1 block text-xs text-negative">{fieldErrors.url}</span>}
          </div>
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={1000}
            placeholder="Note for the brand (optional)"
            aria-label="Note for the brand"
            className="min-w-48 flex-1"
          />
          <Button type="submit" size="sm" disabled={busy} className="h-10">
            {busy ? 'Sending…' : 'Send for approval'}
          </Button>
        </form>
      )}
      <Alert className="mt-2">{error}</Alert>

      {posts.length > 0 && (
        <ul className="mt-3 space-y-2">
          {posts.map((p) => (
            <li key={p.id} className="flex flex-wrap items-center gap-2 text-sm">
              <Badge tone={postStatusTone[p.status]}>{postStatusLabel[p.status]}</Badge>
              <a href={p.url} target="_blank" rel="noopener noreferrer nofollow" className="max-w-md truncate text-accent hover:underline">
                {p.url}
              </a>
              <span className="text-xs text-ink-faint">{new Date(p.createdAt).toLocaleDateString()}</span>
              {p.decisionNote && <span className="text-xs text-ink-muted">“{p.decisionNote}”</span>}
              {p.status === 'submitted' && (
                <Button size="sm" variant="ghost" onClick={() => withdraw(p.id)}>
                  Withdraw
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      <p className="mt-2 text-xs text-ink-faint">
        The brand checks each post and approves it; that is what pays it. Posts can be sent until{' '}
        {POST_SUBMISSION_GRACE_DAYS} days after the program ends.
      </p>
    </div>
  );
}
